import React from "react";
import PlotlyChart from "../PlotlyChart";
import OverpaymentComparisonTable from "./OverpaymentComparisonTable";
import YearlyBreakdownTable from "./YearlyBreakdownTable";

function ResultsPanel({
  result,
  selectedResult,
  setSelectedResult,
  loanPlanLabels,
  planDetails,
  renderTableProps,
}) {
  if (!result) return null;

  const dropdownOptions = Object.keys(result).map((planKey) => ({
    value: planKey,
    label: loanPlanLabels[planKey] || planKey,
  }));

  const selectedPlanResult =
    result && selectedResult ? { ...result[selectedResult], planKey: selectedResult } : null;
  const selectedPlanInput = selectedResult ? planDetails?.[selectedResult] : null;
  const hasOverpaymentComparison = Boolean(selectedPlanResult?.result_no_overpayment);

  if (!selectedPlanResult) return null;

  return (
    <div className="results">
      <div className="results-select-wrapper">
        <div className="input-group centered-results-group">
          <label className="input-label" htmlFor="plan-result-select">
            View results for
          </label>
          <select
            id="plan-result-select"
            className="styled-dropdown"
            value={selectedResult}
            onChange={(e) => setSelectedResult(e.target.value)}
          >
            {dropdownOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="plan-section">
        <div className="centered-label">
          <span>{loanPlanLabels[selectedPlanResult.planKey] || selectedPlanResult.planKey}</span>
        </div>
        <div className="summary-cards">
          <div className="summary-card">
            <span className="summary-label">Total Repayment:</span>
            <span className="summary-value">
              £
              {selectedPlanResult.total_repayment.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Loan Paid / Written Off:</span>
            <span className="summary-value">
              {selectedPlanResult.end_month.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Balance Written Off:</span>
            <span className="summary-value">
              £
              {selectedPlanResult.writeoff_balance.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>

        {hasOverpaymentComparison ? (
          <OverpaymentComparisonTable
            rows={selectedPlanResult.overpayment_comparison_table}
            irr={selectedPlanResult.irr}
            overpaymentType={selectedPlanInput?.overpayment_type || selectedPlanResult.overpayment_type}
            overpaymentAmount={
              selectedPlanInput?.overpayment_amount ?? selectedPlanResult.overpayment_amount
            }
            overpaymentLumpSumWholeThing={Boolean(
              selectedPlanInput?.overpayment_lump_sum_whole_thing
            )}
          />
        ) : null}

        <div className="chart-header">
          <h3 className="chart-title">
            Balance Forecast
            <span className="info-btn" tabIndex={0} aria-label="What is the balance forecast chart?">
              <span className="info-icon">i</span>
              <span className="info-tooltip">
                Shows how your remaining loan balance changes over time.
                If overpayments are selected, it compares baseline repayments with and without overpayments.
              </span>
            </span>
          </h3>
        </div>
        <div className="chart-container">
          <PlotlyChart plotlyJson={selectedPlanResult.closing_balance_chart_json} />
        </div>
        <div className="chart-header">
          <h3 className="chart-title">
            Repayment Forecast
            <span className="info-btn" tabIndex={0} aria-label="What is the repayment forecast chart?">
              <span className="info-icon">i</span>
              <span className="info-tooltip">
                Shows cumulative repayments over time and the monthly amounts behind that total.
                This helps you see when most repayments happen.
                If overpayments are selected, it compares baseline repayments with and without overpayments.
              </span>
            </span>
          </h3>
        </div>
        <div className="chart-container">
          <PlotlyChart plotlyJson={selectedPlanResult.repayment_chart_json} />
        </div>

        <h3 className="centered-label detailed-forecast-title">Detailed Loan Forecast</h3>
        <YearlyBreakdownTable table={selectedPlanResult.yearly_table} planKey={selectedPlanResult.planKey} {...renderTableProps} />
      </div>
    </div>
  );
}

export default ResultsPanel;
